/**
 * On Leave Today Hook
 *
 * Supplies the manager's team members who are on approved leave **today**, for
 * the "On leave today" metric + drill-down modal on the Manager Dashboard.
 *
 * Data flow (source: `/data/LeaveRequestsV2`):
 *   1. Team roster comes from useTeamMembers (EssWorkerManagerRelations).
 *   2. Per member, LeaveRequestsV2 is queried filtered to today's LeaveDate —
 *      a narrow query that returns only the day's leave line(s), not the whole
 *      history.
 *   3. Leave type Description + unit come from the LeaveTypes lookup.
 *   4. The authoritative status is read from EssLeaveRequestHeaders (LeaveStatus)
 *      per RequestId — a member counts as "on leave today" only when that status
 *      is Approved. This mirrors LeaveContext / useTeamLeaveRequests, the proven
 *      path for these entities.
 *
 * Each entry is mapped into the shared card shape the portal uses —
 * { id, personnelNumber, name, role, status, leaveTypeName, requestId,
 *   dataAreaId } — so selecting one can open the shared TeamMemberDetailModal.
 *
 * Returns:
 *   - onLeaveToday: Array<mapped member>
 *   - count: number
 *   - isLoading: boolean
 *   - error: Error | null
 */
import { useQuery } from "react-query";
import { leaveService } from "../../api/services";
import useTeamMembers from "./useTeamMembers";

const APPROVED_STATUSES = new Set(["Approved", "approved"]);

/** Start-of-day / end-of-day ISO bounds for the given date (local day). */
const dayBoundsIso = (referenceDate) => {
  const from = new Date(referenceDate);
  from.setHours(0, 0, 0, 0);
  const to = new Date(referenceDate);
  to.setHours(23, 59, 59, 999);
  return { fromIso: from.toISOString(), toIso: to.toISOString() };
};

/** Local YYYY-MM-DD key for a date, avoiding UTC shift. */
const toLocalKey = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

export default function useOnLeaveToday(referenceDate = new Date()) {
  const {
    members,
    isLoading: membersLoading,
    error: membersError,
  } = useTeamMembers();

  const personnelKey = members.map((m) => m.personnelNumber).join(",");
  const dayKey = toLocalKey(referenceDate);

  const {
    data: onLeaveToday,
    isLoading: leaveLoading,
    error: leaveError,
  } = useQuery(
    ["onLeaveToday", dayKey, personnelKey],
    async () => {
      const { fromIso, toIso } = dayBoundsIso(referenceDate);

      // LeaveType -> Description lookup (best-effort; falls back to raw code).
      const descMap = {};
      try {
        const leaveTypesResponse = await leaveService.getLeaveTypes();
        (leaveTypesResponse.data?.value || []).forEach((lt) => {
          descMap[lt.Type] = lt.Description;
        });
      } catch {
        // optional
      }

      // For each team member, pull only today's LeaveRequestsV2 line(s).
      const perMember = await Promise.all(
        members.map(async (member) => {
          try {
            const response = await leaveService.getLeaveRequestsForEmployeeOnDay(
              member.personnelNumber,
              fromIso,
              toIso
            );
            const lines = response.data?.value || [];
            if (lines.length === 0) return null;

            // A member may have >1 line/request today; take the first request
            // and resolve its authoritative header status.
            const line = lines[0];
            const requestId = line.RequestId;
            const dataAreaId = line.dataAreaId || "usmf";

            let status = line.Status;
            try {
              const headerRes = await leaveService.getEssLeaveRequestHeaderById(
                requestId,
                dataAreaId
              );
              status = headerRes.data?.LeaveStatus || status;
            } catch {
              // fall back to the LeaveRequestsV2 Status
            }

            if (!APPROVED_STATUSES.has(status)) return null;

            const leaveTypeName = descMap[line.LeaveType] || line.LeaveType;

            return {
              id: member.personnelNumber,
              personnelNumber: member.personnelNumber,
              name: member.name,
              role: leaveTypeName || "On Leave",
              status: "On Leave",
              leaveTypeName,
              requestId,
              dataAreaId,
            };
          } catch (err) {
            console.error(
              `❌ Failed to fetch today's leave for ${member.personnelNumber}:`,
              err
            );
            return null;
          }
        })
      );

      return perMember
        .filter(Boolean)
        .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    },
    {
      enabled: members.length > 0,
      retry: 2,
      staleTime: 5 * 60 * 1000,
    }
  );

  return {
    onLeaveToday: onLeaveToday ?? [],
    count: onLeaveToday?.length ?? 0,
    isLoading: membersLoading || (members.length > 0 && leaveLoading),
    error: membersError || leaveError || null,
  };
}
